import React, { useState } from 'react';
import {
  getCharacterCount,
  getCharacterCountNoSpaces,
  getWordCount,
  getSentenceCount,
  getParagraphCount,
  getReadingTime,
  getFleschReadingEase,
  getFleschKincaidGrade,
  getMonosyllabicWordCount,
  getPolysyllabicWordCount,
  getAverageWordLength,
  getAverageSentenceLength,
  getSpaceCount,
  getSyllablesPerWord
} from '../utils/textStats';
import {
  getWordFrequencies,
  getTwoWordFrequencies,
  getThreeWordFrequencies
} from '../utils/frequencyAnalysis';
import OverlayTextArea from './OverlayTextArea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import CharacterCount from './CharacterCount';
import { DeepSeekService } from '../lib/deepseek';

// Initialize DeepSeek service
const deepseek = new DeepSeekService(process.env.REACT_APP_DEEPSEEK_API_KEY);

// Language options for translation
const LANGUAGES = [
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
  { value: 'ru', label: 'Russian' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
];

export default function TextCounter({ markAIUsed }) {
  const [text, setText] = useState('');
  const [aiOutput, setAiOutput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [freqType, setFreqType] = useState('1');
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0].value);

  // Basic stats
  const characterCount = getCharacterCount(text);
  const characterCountNoSpaces = getCharacterCountNoSpaces(text);
  const wordCount = getWordCount(text);
  const sentenceCount = getSentenceCount(text);
  const paragraphCount = getParagraphCount(text);
  const readingTimeRaw = getReadingTime(text).toFixed(2);

  // Frequencies
  let freqArray = [];
  if (freqType === '1') {
    freqArray = getWordFrequencies(text);
  } else if (freqType === '2') {
    freqArray = getTwoWordFrequencies(text);
  } else {
    freqArray = getThreeWordFrequencies(text);
  }
  const topFreqArray = freqArray.slice(0, 10);

  // Advanced stats
  const fleschReadingEaseRaw = getFleschReadingEase(text).toFixed(2);
  const fleschKincaidGradeRaw = getFleschKincaidGrade(text).toFixed(2);
  const monoCount = getMonosyllabicWordCount(text);
  const polyCount = getPolysyllabicWordCount(text);
  const avgWordLengthRaw = getAverageWordLength(text).toFixed(2);
  const avgSentenceLengthRaw = getAverageSentenceLength(text).toFixed(2);
  const spaceCount = getSpaceCount(text);
  const sylPerWordRaw = getSyllablesPerWord(text).toFixed(2);

  // Limit handling
  const [limitType, setLimitType] = useState('char');
  const [limit, setLimit] = useState('');

  const handleClear = () => {
    setText('');
  };
  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  // Show "-" for advanced metrics if text is empty
  const readingTime = wordCount === 0 ? '-' : readingTimeRaw;
  const fleschReadingEase = wordCount === 0 ? '-' : fleschReadingEaseRaw;
  const fleschKincaidGrade = wordCount === 0 ? '-' : fleschKincaidGradeRaw;
  const avgWordLength = wordCount === 0 ? '-' : avgWordLengthRaw;
  const avgSentenceLength = wordCount === 0 ? '-' : avgSentenceLengthRaw;
  const sylPerWord = wordCount === 0 ? '-' : sylPerWordRaw;

  // For the usage line: use characterCount or wordCount depending on limitType
  const currentLength = limitType === 'char' ? characterCount : wordCount;
  const usageLabel = limitType === 'char' ? 'Characters' : 'Words';

  const handleImprovedText = (improvedText) => {
    setText(improvedText);
  };

  const handleSummarize = async () => {
    try {
      setIsLoading(true);
      setError(null);
      markAIUsed();
      const summary = await deepseek.summarize(text);
      setAiOutput(summary);
    } catch (err) {
      setError(`Failed to summarize: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFixGrammar = async () => {
    try {
      setIsLoading(true);
      setError(null);
      markAIUsed();
      const fixed = await deepseek.fixGrammar(text);
      setAiOutput(fixed);
    } catch (err) {
      setError(`Failed to fix grammar: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtractKeywords = async () => {
    try {
      setIsLoading(true);
      setError(null);
      markAIUsed();
      const keywords = await deepseek.extractKeywords(text);
      setAiOutput(keywords);
    } catch (err) {
      setError(`Failed to extract keywords: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtractEntities = async () => {
    try {
      setIsLoading(true);
      setError(null);
      markAIUsed();
      const entities = await deepseek.extractEntities(text);
      setAiOutput(entities);
    } catch (err) {
      setError(`Failed to extract entities: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranslate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      markAIUsed();
      const translation = await deepseek.translate(text, selectedLanguage);
      setAiOutput(translation);
    } catch (err) {
      setError(`Failed to translate: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShorten = async () => {
    try {
      setIsLoading(true);
      setError(null);
      markAIUsed();
      const shortened = await deepseek.shorten(text, limit, limitType);
      setAiOutput(shortened);
    } catch (err) {
      setError(`Failed to shorten: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Basic Metrics Card */}
      <Card>
        <CardContent className="grid grid-cols-5">
          <div className="flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-2xl font-semibold text-text-primary">{characterCount}</span>
            <span className="text-sm text-text-secondary">Characters</span>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-2xl font-semibold text-text-primary">{spaceCount}</span>
            <span className="text-sm text-text-secondary">Spaces</span>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-2xl font-semibold text-text-primary">{wordCount}</span>
            <span className="text-sm text-text-secondary">Words</span>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-2xl font-semibold text-text-primary">{sentenceCount}</span>
            <span className="text-sm text-text-secondary">Sentences</span>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-2xl font-semibold text-text-primary">{paragraphCount}</span>
            <span className="text-sm text-text-secondary">Paragraphs</span>
          </div>
        </CardContent>
      </Card>

      {/* Main Editor Card */}
      <div className="flex gap-4">
        <Card className="flex-[2]">
          <CardHeader>
            <CardTitle>Text Editor</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Limit Controls */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-text-secondary">Set a</span>
                <div className="inline-flex rounded-md border border-border">
                  <Button
                    variant={limitType === 'char' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setLimitType('char')}
                    className="rounded-r-none"
                  >
                    Character
                  </Button>
                  <Button
                    variant={limitType === 'word' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setLimitType('word')}
                    className="rounded-l-none"
                  >
                    Word
                  </Button>
                </div>
                <span className="text-sm text-text-secondary">limit:</span>
                <input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  className="w-20 rounded-md border border-[rgb(99,102,241)] px-2 py-1"
                />
              </div>
              <CharacterCount 
                count={currentLength} 
                limit={limit === '' ? undefined : parseInt(limit, 10)} 
                type={limitType}
              />
            </div>

            {/* Text Area */}
            <div className="space-y-4">
              <div className="min-h-[300px]">
                <OverlayTextArea
                  value={text}
                  onChange={setText}
                  limit={limit === '' ? undefined : parseInt(limit, 10)}
                  limitType={limitType}
                  className="min-h-[300px] w-full"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                {/* AI Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSummarize}
                    disabled={isLoading || !text}
                    className="bg-[rgb(99,102,241)]/10 hover:bg-[rgb(79,70,229)] hover:text-white"
                  >
                    {isLoading ? 'Processing...' : 'Summarize'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleFixGrammar}
                    disabled={isLoading || !text}
                    className="bg-[rgb(99,102,241)]/10 hover:bg-[rgb(79,70,229)] hover:text-white"
                  >
                    {isLoading ? 'Processing...' : 'Fix grammar'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleExtractKeywords}
                    disabled={isLoading || !text}
                    className="bg-[rgb(99,102,241)]/10 hover:bg-[rgb(79,70,229)] hover:text-white"
                  >
                    {isLoading ? 'Processing...' : 'Extract keywords'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleExtractEntities}
                    disabled={isLoading || !text}
                    className="bg-[rgb(99,102,241)]/10 hover:bg-[rgb(79,70,229)] hover:text-white"
                  >
                    {isLoading ? 'Processing...' : 'Extract entities'}
                  </Button>
                  <div className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-[rgb(99,102,241)]/10 hover:bg-[rgb(79,70,229)] hover:text-white"
                      disabled={isLoading || !text}
                    >
                      {isLoading ? 'Processing...' : 'Translate'}
                    </Button>
                    <div className="invisible group-hover:visible absolute left-0 top-full z-50 mt-1 w-40 rounded-md border border-border bg-background py-1 shadow-lg">
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.value}
                          onClick={() => {
                            setSelectedLanguage(lang.value);
                            handleTranslate();
                          }}
                          className="block w-full px-4 py-2 text-left text-sm hover:bg-[rgb(79,70,229)] hover:text-white"
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {limit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleShorten}
                      disabled={isLoading || !text}
                      className="bg-[rgb(99,102,241)]/10 hover:bg-[rgb(79,70,229)] hover:text-white"
                    >
                      {isLoading ? 'Processing...' : 'Shorten'}
                    </Button>
                  )}
                </div>

                {/* Clear & Copy Buttons */}
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={handleClear}>
                    Clear
                  </Button>
                  <Button onClick={handleCopy}>
                    Copy
                  </Button>
                </div>
              </div>

              {error && (
                <div className="text-sm text-negative">
                  {error}
                </div>
              )}

              {/* AI Output */}
              {aiOutput && (
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-text-primary">AI Output</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setText(aiOutput);
                        setAiOutput('');
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                  <OverlayTextArea
                    value={aiOutput}
                    onChange={setAiOutput}
                    className="min-h-[200px] w-full"
                    readOnly
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right Column */}
        <div className="flex flex-1 flex-col gap-4">
          {/* Advanced Metrics Card */}
          <Card>
            <CardHeader>
              <CardTitle as="h3">Advanced Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Reading Time</span>
                    <p className="text-lg font-semibold text-text-primary">{readingTime} min</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Characters (no spaces)</span>
                    <p className="text-lg font-semibold text-text-primary">{characterCountNoSpaces}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Monosyllabic Words</span>
                    <p className="text-lg font-semibold text-text-primary">{monoCount}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Polysyllabic Words</span>
                    <p className="text-lg font-semibold text-text-primary">{polyCount}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Avg Word Length</span>
                    <p className="text-lg font-semibold text-text-primary">{avgWordLength}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Avg Sentence Length</span>
                    <p className="text-lg font-semibold text-text-primary">{avgSentenceLength}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Syllables per Word</span>
                    <p className="text-lg font-semibold text-text-primary">{sylPerWord}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Flesch Reading Ease</span>
                    <p className="text-lg font-semibold text-text-primary">{fleschReadingEase}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-sm text-text-secondary">Flesch-Kincaid Grade</span>
                    <p className="text-lg font-semibold text-text-primary">{fleschKincaidGrade}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Frequency Analysis Card */}
          <Card>
            <CardHeader>
              <CardTitle as="h3">Word Frequency</CardTitle>
              <CardDescription>
                Most common words and phrases
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex gap-2">
                <Button
                  variant={freqType === '1' ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => setFreqType('1')}
                >
                  Single
                </Button>
                <Button
                  variant={freqType === '2' ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => setFreqType('2')}
                >
                  Pairs
                </Button>
                <Button
                  variant={freqType === '3' ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => setFreqType('3')}
                >
                  Triplets
                </Button>
              </div>
              <div className="max-h-[200px] space-y-2 overflow-y-auto">
                {topFreqArray.map(([phrase, count], index) => (
                  <div key={index} className="flex justify-between">
                    <span className="text-sm text-text-secondary">{phrase}</span>
                    <Badge variant="purple">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 